import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { Pencil, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useMembers } from "@/hooks/use-members";
import {
  type QuestionPriority,
  type QuestionStatus,
  useDeleteQuestion,
  usePatchQuestion,
  useQuestion,
} from "@/hooks/use-questions";
import { useStakeholders } from "@/hooks/use-stakeholders";
import { cn } from "@/lib/utils";

const UNASSIGNED = "__unassigned__";

const PRIORITY_LABELS: Record<QuestionPriority, string> = {
  low: "Low",
  med: "Med",
  high: "High",
};

interface QuestionDetailContentProps {
  questionId: string | null;
  solutionId: string;
  /** Called after a successful delete — typically to close the parent sheet. */
  onDeleted?: () => void;
}

/**
 * Pure-content variant of the question detail. No Sheet wrapper — meant to be
 * embedded inside other sheets (e.g. linked-questions slide-out on the Review
 * page) or page sections.
 */
export function QuestionDetailContent({
  questionId,
  solutionId,
  onDeleted,
}: QuestionDetailContentProps) {
  const { data, isLoading } = useQuestion(questionId);
  const patch = usePatchQuestion(solutionId);
  const remove = useDeleteQuestion(solutionId);
  const navigate = useNavigate();
  const stakeholders = useStakeholders(solutionId);
  const members = useMembers(solutionId);

  const assigneeOptions = useMemo(() => {
    const out: { value: string; kind: "member" | "stakeholder"; name: string; sub: string }[] = [];
    for (const m of members.data ?? []) {
      out.push({
        value: `member:${m.userId}`,
        kind: "member",
        name: m.name ?? m.email,
        sub: m.email,
      });
    }
    for (const s of stakeholders.data ?? []) {
      out.push({
        value: `stakeholder:${s.id}`,
        kind: "stakeholder",
        name: s.name,
        sub: `${s.role}${s.org === "client" ? " · Client" : " · Nymbl"}`,
      });
    }
    return out;
  }, [members.data, stakeholders.data]);

  const currentAssigneeValue = data?.assignedToPerson
    ? `${data.assignedToPerson.kind}:${data.assignedToPerson.id}`
    : UNASSIGNED;
  // We only call useParams to know whether we're already on the Questions page,
  // so closing the sheet after delete navigates correctly. Wrapped in try/catch
  // via a route-agnostic match.
  const params = useParams({ strict: false }) as { solutionId?: string };

  const [draftAnswer, setDraftAnswer] = useState<string>("");
  const [draftText, setDraftText] = useState<string>("");
  const [editingText, setEditingText] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (data) {
      setDraftAnswer(data.answer ?? "");
      setDraftText(data.text);
      setEditingText(false);
      setConfirmDelete(false);
    }
  }, [data]);

  function saveAnswer() {
    if (!data) return;
    patch.mutate({
      id: data.id,
      patch: {
        answer: draftAnswer.trim() || null,
        status: draftAnswer.trim() ? "answered" : "open",
      },
    });
  }

  function saveText() {
    if (!data) return;
    const text = draftText.trim();
    if (!text || text === data.text) {
      setEditingText(false);
      return;
    }
    patch.mutate({ id: data.id, patch: { text } }, { onSuccess: () => setEditingText(false) });
  }

  function changeAssignee(value: string) {
    if (!data) return;
    if (value === UNASSIGNED) {
      patch.mutate({
        id: data.id,
        patch: { assignedToStakeholderId: null, assignedToUserId: null },
      });
      return;
    }
    const [kind, id] = value.split(":");
    patch.mutate({
      id: data.id,
      patch: {
        assignedToStakeholderId: kind === "stakeholder" ? (id ?? null) : null,
        assignedToUserId: kind === "member" ? (id ?? null) : null,
      },
    });
  }

  function handleDelete() {
    if (!data) return;
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    remove.mutate(data.id, {
      onSuccess: () => {
        onDeleted?.();
        // If we're on the Questions page (params.solutionId match), strip ?q=<id>.
        if (params.solutionId) {
          navigate({
            to: "/s/$solutionId/questions",
            params: { solutionId: params.solutionId },
            search: (prev: { q?: string }) => ({ ...prev, q: undefined }),
            replace: true,
          });
        }
      },
    });
  }

  if (isLoading) {
    return (
      <div className="p-6 space-y-3">
        <Skeleton className="h-5 w-1/3" />
        <Skeleton className="h-7 w-3/4" />
        <Skeleton className="h-32 w-full mt-4" />
      </div>
    );
  }

  if (!data) {
    return <div className="p-6 text-sm text-muted-foreground">Question not found.</div>;
  }

  return (
    <>
      <header className="border-b border-[var(--color-border)] px-6 py-4">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
          {data.externalKey ? (
            <span className="font-mono tabular-nums">{data.externalKey}</span>
          ) : null}
          <span>·</span>
          <span>{new Date(data.raisedAt).toLocaleDateString()}</span>
          {data.raisedByPerson && (
            <>
              <span>·</span>
              <span>Raised by {data.raisedByPerson.name}</span>
            </>
          )}
          <span
            className={cn(
              "ml-auto inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium",
              data.status === "answered"
                ? "bg-success-bg text-success"
                : "bg-warning-bg text-warning",
            )}
          >
            {data.status === "answered" ? "Answered" : "Open"}
          </span>
        </div>

        {editingText ? (
          <div className="mt-2 space-y-2">
            <Textarea
              value={draftText}
              onChange={(e) => setDraftText(e.target.value)}
              rows={3}
              className="text-sm"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setDraftText(data.text);
                  setEditingText(false);
                }}
                disabled={patch.isPending}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={saveText}
                disabled={patch.isPending || !draftText.trim() || draftText.trim() === data.text}
              >
                Save
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-2 flex items-start gap-2">
            <h3 className="font-display text-base leading-snug flex-1">{data.text}</h3>
            <button
              type="button"
              onClick={() => setEditingText(true)}
              className="text-muted-foreground hover:text-foreground shrink-0 mt-0.5"
              title="Edit question text"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </header>

      <div className="p-6 space-y-5">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground mb-1">
              Priority
            </div>
            <Select
              value={data.priority}
              onValueChange={(value) =>
                patch.mutate({
                  id: data.id,
                  patch: { priority: value as QuestionPriority },
                })
              }
            >
              <SelectTrigger className="h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(PRIORITY_LABELS) as QuestionPriority[]).map((p) => (
                  <SelectItem key={p} value={p}>
                    {PRIORITY_LABELS[p]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground mb-1">
              Status
            </div>
            <Select
              value={data.status}
              onValueChange={(value) =>
                patch.mutate({ id: data.id, patch: { status: value as QuestionStatus } })
              }
            >
              <SelectTrigger className="h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="answered">Answered</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground mb-1">
            Assigned to
          </div>
          <Select value={currentAssigneeValue} onValueChange={changeAssignee}>
            <SelectTrigger className="h-9 text-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={UNASSIGNED}>
                <span className="text-muted-foreground italic">Unassigned</span>
              </SelectItem>
              {assigneeOptions.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  <span className="inline-flex items-center gap-2">
                    <span
                      className={cn(
                        "inline-flex items-center rounded px-1 py-0.5 text-[9px] uppercase tracking-[0.1em] font-semibold",
                        opt.kind === "member"
                          ? "bg-nymbl-sky text-nymbl-navy"
                          : "bg-muted text-foreground/70",
                      )}
                    >
                      {opt.kind === "member" ? "Member" : "Stakeholder"}
                    </span>
                    <span>{opt.name}</span>
                    <span className="text-muted-foreground">— {opt.sub}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {assigneeOptions.length === 0 && (
            <p className="text-[11px] text-muted-foreground mt-1">
              Invite a member or add a stakeholder on the Members page.
            </p>
          )}
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground mb-2">
            Answer
          </div>
          <Textarea
            value={draftAnswer}
            onChange={(e) => setDraftAnswer(e.target.value)}
            placeholder="Document the resolution…"
            rows={5}
            className="text-sm"
          />
          <Button
            size="sm"
            className="mt-2"
            onClick={saveAnswer}
            disabled={patch.isPending || draftAnswer === (data.answer ?? "")}
          >
            Save answer
          </Button>
        </div>

        {data.relatedItemIds.length > 0 && (
          <section>
            <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground mb-2">
              Related items
            </div>
            <ul className="space-y-1.5">
              {data.relatedItemIds.map((id) => (
                <li key={id}>
                  <Link
                    to="/s/$solutionId/review/$itemId"
                    params={{ solutionId, itemId: id }}
                    className="text-sm text-nymbl-blue hover:underline"
                  >
                    {id.slice(0, 8)}…
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="pt-2 border-t border-[var(--color-border)]">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">
              {confirmDelete
                ? "Click again to confirm — this can't be undone."
                : "Delete this question and unlink it from any related items."}
            </p>
            <Button
              size="sm"
              variant={confirmDelete ? "destructive" : "outline"}
              onClick={handleDelete}
              disabled={remove.isPending}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1.5" />
              {remove.isPending ? "Deleting…" : confirmDelete ? "Confirm delete" : "Delete"}
            </Button>
          </div>
        </section>
      </div>
    </>
  );
}
